import { SelfServiceUserAccount } from '@ekt-group/general-purpose-api-interfaces';
import { CustomerContractRole } from '@ekt-group/general-purpose-api-interfaces/src/types/customer-contract-role.enum';
import { SearchLg, X } from '@untitled-ui/icons-react';
import fuzzysort from 'fuzzysort';
import { ChangeEventHandler, ComponentProps, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import Input from '@/components/ui/Input';
import { useUserInfoStore } from '@/store';
import { cn } from '@/utils/cn';

import { UserAccount } from './UserAccount';

interface UserAccountListProps extends ComponentProps<'div'> {
  accounts: SelfServiceUserAccount[];
  onAccountSelect?: (account: SelfServiceUserAccount) => void;

  shouldToggleSearchBar?: boolean;
}

export interface ExtendedSelfServiceUserAccount {
  originalObject: SelfServiceUserAccount & { type: string };
  highlightedObject: SelfServiceUserAccount & { type: string };
}

export const UserAccountList = ({ className, accounts, shouldToggleSearchBar, onAccountSelect }: UserAccountListProps) => {
  const { t } = useTranslation();
  const { selectedAccount, setSelectedAccount } = useUserInfoStore();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isSearchBarActive, setIsSearchBarActive] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const handleSearchButtonClick = () => {
    setIsSearchBarActive(!isSearchBarActive);
  };

  const extendedAccounts = accounts?.map((account) => {
    const type =
      account.role === CustomerContractRole.CompanyRepresentative
        ? t('legalEntity', { ns: 'header' })
        : t('privateClient', { ns: 'header' });

    return {
      originalObject: { ...account, type },
      highlightedObject: { ...account, type },
    };
  });

  const [filteredAccounts, setFilteredAccounts] = useState<ExtendedSelfServiceUserAccount[]>(extendedAccounts ?? []);

  const handleSearchValueChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const searchValue = event.currentTarget.value.trim();

    if (searchValue !== '') {
      setIsSearchBarActive(true);
    }

    const originalObjects = extendedAccounts?.map((account) => account.originalObject);

    const searchResults = fuzzysort
      .go(searchValue.trim(), originalObjects ?? [], {
        keys: ['name', 'code', 'type'],
        all: true,
      })
      .map((result) => {
        const highlight = result.map((result) => result.highlight());

        return {
          originalObject: result.obj,
          highlightedObject: {
            ...result.obj,
            name: highlight[0] || result.obj.name,
            code: highlight[1] || result.obj.code,
            type: highlight[2] || result.obj.type,
          },
        };
      });

    setFilteredAccounts(searchResults);
  };

  const handleClearButtonClick = () => {
    setIsSearchBarActive(false);
    if (inputRef.current) {
      inputRef.current.value = '';
      setFilteredAccounts(extendedAccounts ?? []);
    }
  };

  const handleAccountSelect = (account: SelfServiceUserAccount) => {
    setSearchParams();

    setSelectedAccount(account);
    onAccountSelect?.(account);
  };

  return (
    <div className={cn('space-y-4', className)}>
      <div className={cn('flex justify-between items-center h-[2.4rem] sticky top-0 bg-white z-10')}>
        {shouldToggleSearchBar ? (
          <>
            <Input
              ref={inputRef}
              isVisible={isSearchBarActive}
              placeholder={t('search', { ns: 'header' })}
              onChange={handleSearchValueChange}
              leftSide={<SearchLg className="stroke-brand-" />}
              rightSide={<X className={cn('cursor-pointer text-brand-brown hover:text-black')} onClick={handleClearButtonClick} />}
              className={cn({
                'animate-fade-left': isSearchBarActive,
              })}
            />
            <div className={cn('flex flex-1 justify-between w-full ', { hidden: isSearchBarActive })}>
              {t('chooseAccount', { ns: 'header' })}
              <SearchLg className="cursor-pointer text-brand-brown" onClick={handleSearchButtonClick} />
            </div>
          </>
        ) : (
          <Input
            ref={inputRef}
            placeholder={t('search', { ns: 'header' })}
            onChange={handleSearchValueChange}
            leftSide={<SearchLg className="stroke-brand-" />}
            rightSide={<X className={cn('cursor-pointer text-brand-brown hover:text-black')} onClick={handleClearButtonClick} />}
          />
        )}
      </div>

      <div className="flex flex-col flex-1 overflow-y-auto">
        {filteredAccounts.map((account) => (
          <UserAccount
            key={`${account.originalObject?.code}-${account.highlightedObject?.role}`}
            account={account}
            onClick={handleAccountSelect}
            isSearching={isSearchBarActive}
            isSelected={selectedAccount?.code === account.originalObject.code}
          />
        ))}
        {filteredAccounts.length === 0 && (
          <div className="flex items-center justify-center h-20 text-brand-brown">{t('noResults', { ns: 'header' })}</div>
        )}
      </div>
    </div>
  );
};
